import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Mail } from '@/types/api'
import { formatDateTime } from '@/utils/format'
import type { CombinedColumnDef } from '@/api/dowload-excel.api'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  showUnitColumn: boolean
  canView?: boolean
  canUpdate?: boolean
  canDelete?: boolean
  onViewDetail?: (item: Mail) => void
  onUpdate?: (item: Mail) => void
  onDelete?: (item: Mail) => void
  onToggleStatus?: (item: Mail) => void
}

export function buildColumns({
  pageIndex,
  pageSize,
  showUnitColumn,
  canView = true,
  canUpdate = true,
  canDelete = true,
  onViewDetail,
  onUpdate,
  onDelete,
  onToggleStatus,
}: BuildColumnsOptions): CombinedColumnDef<Mail>[] {
  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      align: 'center',
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
    },
    {
      accessorKey: 'mail',
      header: 'ĐỊA CHỈ MAIL',
      align: 'left',
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a]"
          onClick={() => onViewDetail?.(row.original)}
        >
          {row.original.mail}
        </button>
      ),
    },
    {
      accessorKey: 'name',
      header: 'TÊN HIỂN THỊ',
      align: 'left',
      cell: ({ getValue }) => getValue(),
    },
    {
      accessorKey: 'serverPort',
      header: 'CỔNG',
      align: 'center',
      size: 80,
      cell: ({ getValue }) => getValue(),
    },
    {
      accessorKey: 'protocol',
      header: 'GIAO THỨC',
      align: 'left',
      size: 120,
      cell: ({ getValue }) => {
        const val = getValue()
        return val === 1 ? 'STARTTLS' : val === 2 ? 'SSL/TLS' : '-'
      },
    },
    ...(showUnitColumn
      ? [
          {
            accessorKey: 'unitName',
            header: 'ĐƠN VỊ',
            align: 'left',
            cell: ({ getValue }) => getValue() || '-',
          } satisfies CombinedColumnDef<Mail>,
        ]
      : []),
    {
      accessorKey: 'updatedAt',
      header: 'NGÀY CẬP NHẬT',
      size: 140,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue()),
      exportFormatter: (v) => formatDateTime(v),
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI',
      size: 110,
      align: 'center',
      cell: ({ row }) => {
        const item = row.original
        return (
          <div className="flex justify-center">
            <Switch
              checked={item.status === 1}
              disabled={!canUpdate}
              onCheckedChange={() => {
                if (!canUpdate) return
                onToggleStatus?.(item)
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'actions',
      size: 100,
      exportable: false,
      meta: { fixed: 'right' },
      align: 'center',
      cell: ({ row }) => (
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {canUpdate && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onUpdate?.(row.original)}
                >
                  Cập nhật
                </DropdownMenuItem>
              )}
              {canView && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onViewDetail?.(row.original)}
                >
                  Xem chi tiết
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  onClick={() => onDelete?.(row.original)}
                >
                  Xóa
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]
}
